import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';
import {
  addDayOffset,
  dateCompare,
  DateRange,
  dateRangesOverlap,
  DateString,
  daysBetween,
  lastSundayDate,
  maxDate,
} from '../../../../../common/src/date_string.ts';
import {
  IntervalSongList,
  IntervalType,
  OrgType,
  SongListEntryType,
  SongListSchema,
  SongListSchemaEntry,
} from '../../../../../common/src/model.ts';
import { hasInterval } from '../../../../../common/src/model_util.ts';
import { ensureUnreachable } from '../../../../../common/src/util.ts';
import PencilIcon from '../../../assets/pencil.svg?react';
import TrashIcon from '../../../assets/trash.svg?react';
import { synchronizeSchoolTerms, useCurrentOrg } from '../../../data/use_current_org.tsx';
import { alert } from '../../../util/confirm.tsx';
import { CloseButton } from '../../../util/shared.tsx';
import { HeaderButtonBorderRadius, HeaderButtonPaddingDesktop } from '../../../util/upload_song_button.tsx';
import { useCurrentDate } from '../../../util/use_current_date.tsx';
import { Button } from '../../onboarding/button.tsx';
import { PlannerButtonIconThreshold } from './shared.tsx';
import { AllSongLists, getSchoolSongListSchema, useAllSongLists } from './use_all_song_lists.ts';

export function EditTermsButton({ styled }: { styled: boolean }) {
  const { currentOrg: school } = useCurrentOrg();
  const onClick = useCallback(async () => {
    if (!school) {
      return;
    }
    await openTermPlanner({ _unused: 'hi' });
  }, [school]);

  if (school?.type !== OrgType.School) {
    return null;
  }

  return styled ? (
    <div>
      <EditTermsButtonText onClick={onClick}>Edit Terms</EditTermsButtonText>
      <EditTermsButtonIconWrapper>
        <EditTermsButtonIcon onClick={onClick} />
      </EditTermsButtonIconWrapper>
    </div>
  ) : (
    <EditTermsButtonWrapper onClick={onClick}>Edit Terms</EditTermsButtonWrapper>
  );
}

// TODO(hewitt): eliminate the _unused parameter?
const TermPlanner: ConfirmDialog<{ _unused: string }, object> = ({ proceed, show }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { currentOrg: school } = useCurrentOrg();
  const allSongLists = useAllSongLists();
  const today = useCurrentDate();
  const terms = (
    allSongLists.filter((list) => hasInterval(list) && list.interval.type === IntervalType.Custom) as IntervalSongList[]
  ).sort((lhs, rhs) => dateCompare(rhs.interval.start, lhs.interval.start));

  useEffect(() => {
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef]);

  const onClose = useCallback(() => {
    void synchronizeSchoolTerms(school);
    proceed({});
  }, [proceed, school]);

  const onAddTerm = useCallback(async () => {
    if (!school) {
      return;
    }
    const start =
      terms.length > 0 ? addDayOffset(maxDate(terms.map(({ interval }) => interval.end)), 1) : lastSundayDate(today);
    const end = addDayOffset(start, 6);
    await allSongLists.addCustomInterval({ start, end }); // calls allocateNewSongListId
  }, [allSongLists, school, terms, today]);

  return (
    <Dialog ref={dialogRef}>
      <DialogContent>
        <CloseButton onClick={onClose} isFarFromRight={true} />
        <TermScrollWrapper>
          <TermList>
            {terms.map((term) => (
              <TermRow key={term.id} allSongLists={allSongLists} term={term} />
            ))}
          </TermList>
        </TermScrollWrapper>
        <Footer>
          <ButtonWrapper>
            <Button onClick={onAddTerm}>{'Add Term'}</Button>
          </ButtonWrapper>
          <SongFrequencies allSongLists={allSongLists} />
        </Footer>
      </DialogContent>
    </Dialog>
  );
};

export const openTermPlanner = createConfirmation(confirmable(TermPlanner));

const Dialog = styled.dialog`
  display: flex;
  border: none;
  border-radius: 20px;
  position: fixed;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 20px;
  margin: auto;
  background: var(--color-background);
  top: 0;
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px;
`;

const EditTermsButtonWrapper = styled.div`
  cursor: pointer;
  text-wrap: nowrap;
`;

const EditTermsButtonText = styled(EditTermsButtonWrapper)`
  color: var(--color-primary-button);
  font-family: Jost-SemiBold, Arial, sans-serif;
  border: 3px solid var(--color-primary-button);
  border-radius: ${HeaderButtonBorderRadius};
  background-color: var(--color-background);
  padding: ${HeaderButtonPaddingDesktop};
  @media (max-width: ${PlannerButtonIconThreshold}) {
    display: none;
  }
`;

const EditTermsButtonIconWrapper = styled.div`
  border-radius: 50%;
  border: 3px solid var(--color-primary-button);
  padding: 7px;
  cursor: pointer;
  @media (min-width: ${PlannerButtonIconThreshold}) {
    display: none;
  }
`;

const EditTermsButtonIcon = styled(PencilIcon)`
  width: 15px;
  fill: var(--color-primary-button);
`;

const TermScrollWrapper = styled.div`
  flex-shrink: 1;
  overflow-y: scroll;
`;

const TermList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

function TermRow({ allSongLists, term }: { allSongLists: AllSongLists; term: IntervalSongList }) {
  const { interval } = term;
  const [start, setStart] = useState<DateString | undefined>(() => interval?.start);
  const [end, setEnd] = useState<DateString | undefined>(() => interval?.end);
  const [rangeValidationError, setRangeValidationError] = useState<string | undefined>();

  const onDelete = useCallback(async () => {
    await allSongLists.remove(term);
  }, [allSongLists, term]);

  const onStartChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setStart(event.target.value), []);
  const onEndChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setEnd(event.target.value), []);

  const validateRanges = useCallback(
    (range: DateRange): string | undefined => {
      if (range.end < range.start) {
        return 'Start date must precede end date';
      }
      if (daysBetween(range.start, range.end) + 1 < 7) {
        return 'Terms must be at least 7 days';
      }
      const otherTerms = allSongLists.filter(
        (songList) =>
          hasInterval(songList) && songList.interval.type === IntervalType.Custom && songList.id !== term.id,
      ) as IntervalSongList[];
      for (const otherTerm of otherTerms) {
        if (dateRangesOverlap(otherTerm.interval, range)) {
          return 'Term overlaps another term';
        }
      }
    },
    [allSongLists, term.id],
  );

  const commitDateChanges = useCallback(() => {
    if (!start || !end || validateRanges({ start, end })) {
      return;
    }
    if (start === interval.start && end === interval.end) {
      return;
    }
    void allSongLists.update({
      ...term,
      interval: { type: interval.type, start, end },
    });
  }, [allSongLists, end, interval, start, term, validateRanges]);

  useEffect(() => {
    const newValue = start && end && validateRanges({ start, end });
    if (newValue !== rangeValidationError) {
      // TODO(hewitt): Not worth understanding this logic at this time, but there must be a better way
      // eslint-disable-next-line
      setRangeValidationError(newValue);
      if (!newValue) {
        commitDateChanges();
      }
    }
  }, [end, validateRanges, start, rangeValidationError, commitDateChanges]);

  const onNameBlur = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === term.name) {
        return;
      }
      await allSongLists.update({ ...term, name: event.target.value });
    },
    [allSongLists, term],
  );

  return (
    <TermWrapper>
      <TermName key={'name'} defaultValue={term?.name} placeholder={'<Term Name>'} onBlur={onNameBlur} />
      <TermDateErrorWrapper key={'date-wrapper'}>
        <TermDates key={'term-dates'}>
          <DateInput
            key={'start'}
            type="date"
            defaultValue={interval?.start}
            required
            $valid={!rangeValidationError}
            onChange={onStartChange}
            onBlur={commitDateChanges}
          />
          <ToWrapper key={'dash'}>-</ToWrapper>
          <DateInput
            key={'end'}
            type="date"
            defaultValue={interval?.end}
            required
            $valid={!rangeValidationError}
            onChange={onEndChange}
            onBlur={commitDateChanges}
          />
        </TermDates>
        {rangeValidationError && <TermErrorMessage key={'error'}>{rangeValidationError}</TermErrorMessage>}
      </TermDateErrorWrapper>
      <DeleteButton onClick={onDelete} />
    </TermWrapper>
  );
}

const TermDateErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TermErrorMessage = styled.div`
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: 1rem;
  color: var(--color-invalid-input);
`;

const TermWrapper = styled.div`
  min-height: 50px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 5px;
  align-items: start;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; // TODO(hewitt): determine why ellipsis is not working
  text-align: left;
  padding: 10px 15px;
`;

const TermName = styled.input`
  border-color: transparent;
  background-color: transparent;
  &:hover {
    border-color: var(--color-text);
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 25px;
`;

const TermDates = styled.div`
  display: flex;
  gap: 8px;
`;

const DateInput = styled.input<{ $valid: boolean }>`
  z-index: 1;
  width: 12.25ch;
  border-color: transparent;
  background-color: transparent;
  cursor: text;
  &:hover {
    ${(props) => (props.$valid ? 'border-color: var(--color-text)' : '')}
  }
  &:invalid {
    border-color: var(--color-invalid-input);
  }
  ${(props) => (props.$valid ? '' : 'border-color: var(--color-invalid-input)')}
`;

const ToWrapper = styled.span``;

const SongFrequencyWrapper = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 25px;
`;

const SongsLabel = styled.label`
  text-align: right;
`;

const SongOptions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
`;

function SongOption({
  interval,
  value,
  setValue,
}: {
  interval: IntervalType;
  value?: number;
  setValue: (value: number | undefined) => boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const onBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!setValue(Number(event.target.value) || undefined) && inputRef.current) {
        inputRef.current.value = value?.toString() ?? '0';
      }
    },
    [setValue, value],
  );
  return (
    <SongOptionWrapper>
      <SongCountInput defaultValue={value ?? 0} onBlur={onBlur} ref={inputRef} />
      <SongOptionLabel>per {interval === IntervalType.Custom ? 'term' : interval}</SongOptionLabel>
    </SongOptionWrapper>
  );
}

const SongOptionWrapper = styled.div`
  display: flex;
  gap: 6px;
`;

const SongCountInput = styled.input`
  width: 3ch;
  text-align: center;
`;

const SongOptionLabel = styled.span``;

// this class is only needed to avoid `first-of-type` primary button suppression in <Button> class
const ButtonWrapper = styled.div`
  justify-content: center;
  width: 15ch;
`;

function countRowsWithInterval(songListSchema: SongListSchema, intervalType: IntervalType): number {
  return songListSchema.rows.filter(({ intervalType: type }) => type === intervalType).length;
}

function getMaxRowNumber(songListSchema: SongListSchema | undefined, intervalType: IntervalType): number {
  const rows = (songListSchema?.rows ?? []).filter(({ intervalType: type }) => type === intervalType);
  if (rows.length > 0) {
    return Math.max(...rows.map(({ row }) => row));
  }
  switch (intervalType) {
    case IntervalType.Custom:
      return 0; // row numbers are 1-based
    case IntervalType.Month:
      return getMaxRowNumber(songListSchema, IntervalType.Custom);
    case IntervalType.Week:
      return getMaxRowNumber(songListSchema, IntervalType.Month);
    case IntervalType.Year:
    case IntervalType.Fixed:
    case IntervalType.Season:
      throw new Error(`Schools do not support interval type ${intervalType}`);
    default:
      ensureUnreachable(intervalType);
  }
}

function getRowsBefore(songListSchema: SongListSchema | undefined, intervalType: IntervalType): SongListSchemaEntry[] {
  const rows = songListSchema?.rows ?? [];
  switch (intervalType) {
    case IntervalType.Custom:
      return [];
    case IntervalType.Month:
      return rows.filter(({ intervalType: type }) => type === IntervalType.Custom);
    case IntervalType.Week:
      return rows.filter(({ intervalType: type }) => type === IntervalType.Custom || type === IntervalType.Month);
    case IntervalType.Year:
    case IntervalType.Fixed:
    case IntervalType.Season:
      throw new Error(`Schools do not support interval type ${intervalType}`);
    default:
      ensureUnreachable(intervalType);
  }
}

function getRowsAfter(
  songListSchema: SongListSchema | undefined,
  intervalType: IntervalType,
  rowIndexOffset: number,
): SongListSchemaEntry[] {
  const rows = songListSchema?.rows ?? [];
  let followingIntervalTypes: IntervalType[] = [];
  switch (intervalType) {
    case IntervalType.Custom:
      followingIntervalTypes = [IntervalType.Month, IntervalType.Week];
      break;
    case IntervalType.Month:
      followingIntervalTypes = [IntervalType.Week];
      break;
    case IntervalType.Week:
      followingIntervalTypes = [];
      break;
    case IntervalType.Year:
    case IntervalType.Fixed:
    case IntervalType.Season:
      throw new Error(`Schools do not support interval type ${intervalType}`);
    default:
      ensureUnreachable(intervalType);
  }
  return rows
    .filter(({ intervalType: type }) => type && followingIntervalTypes.includes(type))
    .map((row) => ({ ...row, row: row.row + rowIndexOffset }));
}

const DeleteButton = styled(TrashIcon)`
  margin-right: 5px;
  fill: var(--color-trash);
  flex-shrink: 0;
  background-color: var(--color-background);
  padding: 4px;
  border-radius: 10px;
  width: 35px;
  height: 30px;
  cursor: pointer;
  &:hover {
    fill: var(--color-background);
    background-color: var(--color-trash);
  }
`;

function SongFrequencies({ allSongLists }: { allSongLists: AllSongLists }) {
  const { currentOrg: school, updateOrgInfo: updateSchoolInfo } = useCurrentOrg();
  const songListSchema = getSchoolSongListSchema(school, allSongLists);
  const termFrequency = countRowsWithInterval(songListSchema, IntervalType.Custom);
  const monthFrequency = countRowsWithInterval(songListSchema, IntervalType.Month);
  const weekFrequency = countRowsWithInterval(songListSchema, IntervalType.Week);

  const setFrequency = useCallback(
    async (intervalType: IntervalType, value: number | undefined) => {
      value ??= 0;
      if (!school) {
        return;
      }
      const intervalRows = (songListSchema?.rows ?? []).filter(({ intervalType: type }) => type === intervalType);
      const difference = value - intervalRows.length;
      if (difference === 0) {
        return;
      }
      const maxRowNumber = getMaxRowNumber(songListSchema, intervalType);
      const rowsBefore = getRowsBefore(songListSchema, intervalType);
      const updatedIntervalRows: SongListSchemaEntry[] =
        difference < 0
          ? intervalRows.filter(({ row }) => row <= maxRowNumber + difference)
          : [
              ...intervalRows,
              ...[...Array(difference).keys()].map((index) => ({
                row: index + maxRowNumber + 1,
                type: SongListEntryType.Song,
                intervalType,
              })),
            ];
      const rowsAfter = getRowsAfter(songListSchema, intervalType, difference);

      await updateSchoolInfo({
        id: school.id,
        churchLiturgySchema: {
          ...songListSchema,
          rows: [...rowsBefore, ...updatedIntervalRows, ...rowsAfter],
        },
      });
      if (intervalType === IntervalType.Week) {
        return;
      }
      const intervalsToShift =
        intervalType === IntervalType.Custom ? [IntervalType.Month, IntervalType.Week] : [IntervalType.Week];
      await allSongLists.updateRowIndices({
        rowIndex: maxRowNumber,
        offset: difference,
        intervalTypes: intervalsToShift,
      });
    },
    [allSongLists, school, songListSchema, updateSchoolInfo],
  );

  const showAlert = useCallback(() => {
    void alert({ confirmation: <span>Must specify at least one song</span> });
    return false;
  }, []);

  const setTermFrequency = useCallback(
    (value: number | undefined) => {
      if (!value && monthFrequency === 0 && weekFrequency === 0) {
        return showAlert();
      }
      void setFrequency(IntervalType.Custom, value);
      return true;
    },
    [monthFrequency, setFrequency, showAlert, weekFrequency],
  );

  const setMonthFrequency = useCallback(
    (value: number | undefined) => {
      if (termFrequency === 0 && !value && weekFrequency === 0) {
        return showAlert();
      }
      void setFrequency(IntervalType.Month, value);
      return true;
    },
    [setFrequency, showAlert, termFrequency, weekFrequency],
  );

  const setWeekFrequency = useCallback(
    (value: number | undefined) => {
      if (termFrequency === 0 && monthFrequency === 0 && !value) {
        return showAlert();
      }
      void setFrequency(IntervalType.Week, value);
      return true;
    },
    [monthFrequency, setFrequency, showAlert, termFrequency],
  );

  return (
    <SongFrequencyWrapper>
      <SongsLabel>Songs:</SongsLabel>
      <SongOptions>
        <SongOption interval={IntervalType.Custom} value={termFrequency} setValue={setTermFrequency} />
        <SongOption interval={IntervalType.Month} value={monthFrequency} setValue={setMonthFrequency} />
        <SongOption interval={IntervalType.Week} value={weekFrequency} setValue={setWeekFrequency} />
      </SongOptions>
    </SongFrequencyWrapper>
  );
}
